// Market analytics — App Store (Connect + webhook purchases) and the marketing
// site. These reuse existing admin endpoints; App Store Connect data depends on
// the founder having configured ASC API keys, so shapes are read defensively
// and the views degrade to a "not connected" state when absent.

import { apiDelete, apiGet, apiPost } from './client';

function dateRange(days: number): { startDate: string; endDate: string } {
  const end = new Date();
  const start = new Date(end.getTime() - days * 86400000);
  // Full ISO timestamps: a date-only endDate parses as midnight UTC and
  // silently excludes everything that happened today.
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
  };
}

export type AscPeriod = '7d' | '30d' | '90d';

// The Market view's period control ('7d'/'30d'/'90d'/'all'); 'all' has no
// direct AscPeriod equivalent (ASC overview caps at 90d) but drives the
// webhook-stats calls via an omitted startDate.
export type MarketPeriod = AscPeriod | 'all';

export interface AscChange {
  value?: number;
  percentage?: number | string;
}

// Mirrors ApproxUsdConversion (API src/common/utils/currency-conversion.util.ts)
// — a fixed, hand-maintained rate table, not a live feed. Always render
// ratesAsOf next to approxUsd, and never silently drop unconverted currencies.
export interface ApproxUsdConversion {
  approxUsd: number | null;
  ratesAsOf: string;
  unconverted: string[];
}

export interface AscOverview {
  period: string;
  downloads?: {
    total: number;
    firstTime?: number;
    redownloads?: number;
    updates?: number;
    change?: AscChange;
  };
  sales?: {
    // ASC Sales report rows carry per-unit proceeds in the row's own currency;
    // different rows can be different currencies, so totals are kept as
    // per-currency arrays rather than summed into one number.
    totalProceeds?: AppStoreRevenueAmount[];
    iapRevenue?: AppStoreRevenueAmount[];
    totalUnits?: number;
    // Units delta, not money — never render this as a dollar change.
    unitsChange?: number | null;
    // totalProceeds/iapRevenue collapsed to one approximate USD figure.
    approxUsdProceeds?: ApproxUsdConversion;
    approxUsdIapRevenue?: ApproxUsdConversion;
    // Sales rows whose currency column didn't parse to a recognized ISO
    // code (e.g. a shifted-column report) — excluded from every total above.
    invalidCurrencyRows?: number;
  };
  usage?: {
    activeDevices?: number;
    sessions?: number;
    installs?: number;
    change?: AscChange;
  };
  [key: string]: unknown;
}

// Mirrors MetricTypeStatus (app-store-connect/dto/app-store-connect.dto.ts):
// per stored metricType, what Apple actually delivered on the most recent
// day and which fields the aggregation managed to populate from it.
export interface AscMetricTypeStatus {
  metricType: string;
  mostRecentDate: string | null;
  reportNames: string[];
  unmatchedReports: { name: string; rows: number }[];
  populatedFields: string[];
}

export interface AscStatus {
  configured?: boolean;
  lastSalesReportDate?: string | null;
  lastAnalyticsReportDate?: string | null;
  recentMetrics?: AscMetricTypeStatus[];
  [key: string]: unknown;
}

export interface AppStoreRevenueAmount {
  amount: number;
  currency: string;
}

// Mirrors NotificationStatsResponse (app-store-webhooks/dto/admin-query.dto.ts)
// — totalRevenue is a per-currency array, not a single number.
export interface AppStoreStats {
  totalNotifications?: number;
  totalPurchases?: number;
  totalRevenue?: AppStoreRevenueAmount[];
  // totalRevenue collapsed to one approximate USD figure via the fixed rate table.
  totalRevenueApproxUsd?: ApproxUsdConversion;
  totalRefunds?: number;
  purchasesByCountry?: { storefront: string; count: number }[];
  purchasesByType?: { notificationType: string; count: number }[];
  averageDaysToConvert?: number | null;
  familySharedCount?: number;
  directPurchaseCount?: number;
  [key: string]: unknown;
}

// Mirrors WebAnalyticsService.getOverview (src/web-analytics/web-analytics.service.ts)
// — wire shape is snake_case, verified live 2026-08-10. unique_visitors is a
// sum of per-hour unique SESSIONS, not distinct people — always render it
// labeled "Sessions", never "Unique visitors".
export interface WebOverviewTopPage {
  path: string;
  views: number;
}

export interface WebOverviewDevice {
  device_type: string;
  count: number;
  [key: string]: string | number | null | undefined;
}

export interface WebOverview {
  total_views: number;
  unique_visitors: number;
  top_pages: WebOverviewTopPage[];
  devices: WebOverviewDevice[];
  avg_duration_seconds: number;
  // Formatted percentage string ("0.00"), already bounded 0-100 server-side
  // (engaged_visitors can never exceed total_visitors).
  engagement_rate: string;
  [key: string]: unknown;
}

export interface WebRow {
  [key: string]: string | number | null | undefined;
}

export const getAscStatus = (): Promise<AscStatus> =>
  apiGet<AscStatus>('/admin/app-store-connect/status');

export const getAscOverview = (period: AscPeriod): Promise<AscOverview> =>
  apiGet<AscOverview>('/admin/app-store-connect/overview', { period });

export const getAppStoreStats = (
  params: {
    productId?: string;
    environment?: 'Production' | 'Sandbox';
    startDate?: string;
    endDate?: string;
  } = {},
): Promise<AppStoreStats> => apiGet<AppStoreStats>('/admin/app-store/stats', params);

export const getWebOverview = (days: number): Promise<WebOverview> =>
  apiGet<WebOverview>('/web-analytics/admin/overview', dateRange(days));

export const getWebTopPages = (days: number): Promise<WebRow[]> =>
  apiGet<WebRow[]>('/web-analytics/admin/top-pages', dateRange(days));

export const getWebTrafficSources = (days: number): Promise<WebRow[]> =>
  apiGet<WebRow[]>('/web-analytics/admin/traffic-sources', dateRange(days));

export const getWebCtaPerformance = (days: number): Promise<WebRow[]> =>
  apiGet<WebRow[]>('/web-analytics/admin/cta-performance', dateRange(days));

export type AppStoreTestNotificationEnvironment = 'production' | 'sandbox';

export interface AppStoreTestNotificationResult {
  testNotificationToken: string;
  environment: string;
}

export const requestAppStoreTestNotification = (
  environment: AppStoreTestNotificationEnvironment = 'production',
): Promise<AppStoreTestNotificationResult> =>
  apiPost<AppStoreTestNotificationResult>(
    `/admin/app-store/request-test-notification?environment=${encodeURIComponent(environment)}`,
  );

export interface AscResyncResult {
  [key: string]: unknown;
}

// Re-pulls the App Store Connect Sales (Summary) report for the trailing
// `days` window — used to repair rows ingested with a shifted column mapping.
export const resyncAppStoreConnectData = (days = 90): Promise<AscResyncResult> =>
  apiPost<AscResyncResult>(
    `/admin/app-store-connect/resync?days=${encodeURIComponent(String(days))}`,
  );

// Mirrors NotificationResponseItem (app-store-webhooks/dto/admin-query.dto.ts).
export interface AppStoreNotificationRow {
  id: string;
  notificationUUID: string;
  notificationType: string;
  subtype: string | null;
  environment: string;
  productId: string | null;
  purchaseType: string | null;
  transactionId: string | null;
  originalTransactionId: string | null;
  price: number | null;
  currency: string | null;
  storefront: string | null;
  inAppOwnershipType: string | null;
  purchaseDate: string | null;
  originalPurchaseDate: string | null;
  daysSinceDownload: number | null;
  offerType: number | null;
  offerIdentifier: string | null;
  createdAt: string;
}

export interface PageMeta {
  page: number;
  limit: number;
  totalItems: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

// Mirrors PaginatedNotificationsResponse.
export interface PaginatedAppStoreNotifications {
  data: AppStoreNotificationRow[];
  meta: PageMeta;
}

// Params match GET /admin/app-store/notifications (NotificationQueryDto)
// exactly — page, limit, notificationType, environment, startDate, endDate,
// plus productId/storefront/purchaseType/inAppOwnershipType/transactionId/
// originalTransactionId/search/sortBy/sortDirection (unused here).
export interface AppStoreNotificationsParams {
  page?: number;
  limit?: number;
  notificationType?: string;
  environment?: string;
  startDate?: string;
  endDate?: string;
}

export const getAppStoreNotifications = (
  params: AppStoreNotificationsParams = {},
): Promise<PaginatedAppStoreNotifications> =>
  apiGet<PaginatedAppStoreNotifications>('/admin/app-store/notifications', params);

export interface AppleAdsAttributionSummary {
  capturedInstalls: number;
  attributedInstalls: number;
  unattributedInstalls: number;
  attributionRate: number | null;
  verifiedPaidCustomers: number;
  attributedPaidCustomers: number;
  paidCustomerAttributionRate: number | null;
  attributedInstallToPaidRate: number | null;
  spendUsd: number;
  // False when no ad spend has been entered for the window — cost figures
  // are null in that case, which is not the same as "acquired for free".
  spendLogged?: boolean;
  installCpaUsd: number | null;
  paidCustomerCacUsd: number | null;
  attributedGrossRevenue: ApproxUsdConversion;
  grossRoas: number | null;
}

export interface AppleAdsCampaignPerformance {
  campaignId: string;
  campaignName: string | null;
  installCount: number;
  paidCustomers: number;
  spendUsd: number;
  paidCustomerCacUsd: number | null;
}

export interface AppleAdsAttributionRow {
  id: string;
  attribution: boolean;
  campaignId: string | null;
  adGroupId: string | null;
  keywordId: string | null;
  adId: string | null;
  conversionType: string | null;
  claimType: string | null;
  countryOrRegion: string | null;
  supplyPlacement: string | null;
  clickDate: string | null;
  impressionDate: string | null;
  appVersion: string | null;
  buildNumber: string | null;
  firstReceivedAt: string;
  lastReceivedAt: string;
  paidCustomer: boolean;
  productId: string | null;
  purchaseDate: string | null;
}

export interface AppleAdsSpendRow {
  id: string;
  date: string;
  amountUsd: number;
  campaignId: string | null;
  campaignName: string | null;
  adGroupId: string | null;
  keywordId: string | null;
  source: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AppleAdsReport {
  generatedAt: string;
  window: { startDate: string | null; endDate: string | null };
  summary: AppleAdsAttributionSummary;
  campaigns: AppleAdsCampaignPerformance[];
  recentAttributions: AppleAdsAttributionRow[];
  spend: AppleAdsSpendRow[];
}

export interface UpsertAppleAdsSpendInput {
  date: string;
  amountUsd: number;
  campaignId?: string;
  campaignName?: string;
  adGroupId?: string;
  keywordId?: string;
  notes?: string;
}

export const getAppleAdsReport = (
  params: { startDate?: string; endDate?: string } = {},
): Promise<AppleAdsReport> => apiGet<AppleAdsReport>('/admin/apple-ads/report', params);

export const upsertAppleAdsSpend = (
  input: UpsertAppleAdsSpendInput,
): Promise<AppleAdsSpendRow> =>
  apiPost<AppleAdsSpendRow>('/admin/apple-ads/spend', input);

export const deleteAppleAdsSpend = (id: string): Promise<{ deleted: boolean }> =>
  apiDelete<{ deleted: boolean }>(`/admin/apple-ads/spend/${encodeURIComponent(id)}`);

// Apple Ads (Search Ads) automatic spend import. Mirrors the ASC resync pair:
// a fire-and-forget sync plus a status read that reports whether credentials
// are configured and what the last run did.
export interface AppleSearchAdsStatus {
  configured: boolean;
  errors: string[];
  lastSyncedAt?: string | null;
  lastSyncedDays?: number | null;
  rowsWritten?: number | null;
  manualRowsSuperseded?: number | null;
  skippedUnconvertedCurrency?: number | null;
  /** Apple's own explanation from the last failed run. */
  lastError?: string | null;
  lastErrorAt?: string | null;
  [key: string]: unknown;
}

export const getAppleSearchAdsStatus = (): Promise<AppleSearchAdsStatus> =>
  apiGet<AppleSearchAdsStatus>('/admin/apple-search-ads/status');

export const syncAppleSearchAdsSpend = (days = 30): Promise<{ started: boolean; days: number }> =>
  apiPost<{ started: boolean; days: number }>(
    `/admin/apple-search-ads/sync?days=${encodeURIComponent(String(days))}`,
  );
